<template>
  <div>
    <div v-if="triggerStatus === undefined">
      <NumeraiLoader />
    </div>
    <div v-else>
      <PickleUploadLogModal
        :dialog="showLogModal"
        :on-close="() => (showLogModal = false)"
        :logs="logs"
        :error-description="errorDescription"
      />
      <div
        v-if="
          hideContext &&
            (validationStatus === 'failed' ||
              errorTriggerStatus.includes(triggerStatus))
        "
        class="d-flex"
      >
        <div class="flex_col align_center">
          <StatusChip
            :text="formatStatusName(triggerStatus)"
            status="error"
            :fixed-width="true"
          />
          <div v-if="!hideLogs" class="mt-2">
            <a @click="openTriggerLogs">View logs</a>
          </div>
        </div>
      </div>
      <div v-else :class="{ 'd-flex': !showAll, 'py-3': !showAll }">
        <template v-for="(trigger, index) in computedTriggerStatus">
          <div
            :key="index"
            :class="{
              rowContainer: showAll,
              flex_col: !showAll,
              align_center: !showAll,
            }"
          >
            <StatusChip
              :loading="
                trigger.status === triggerStatus &&
                  !errorTriggerStatus.includes(triggerStatus) &&
                  triggerStatus !== 'submission_succeeded'
              "
              :text="formatStatusName(trigger.status)"
              :status="getStatusClass(trigger.status, index)"
              :fixed-width="true"
              :right-padding="!hideContext"
            />
            <p
              v-if="
                !hideContext && !errorTriggerStatus.includes(trigger.status)
              "
              class="mb-0"
            >
              {{ statusText(trigger.status) }}
            </p>
            <div v-if="!hideLogs && hideContext" class="mt-2">
              <a @click="openTriggerLogs">View logs</a>
            </div>
          </div>
        </template>
        <div v-if="!hideLogs && !hideContext" class="mt-2">
          <a @click="openTriggerLogs"><p>View logs</p></a>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapActions } from 'vuex'
export default {
  components: {
    NumeraiLoader: () => import('@/components/shared/NumeraiLoader'),
    PickleUploadLogModal: () =>
      import('@/components/shared/PickleUploadLogModal'),
    StatusChip: () => import('@/components/shared/StatusChip'),
  },
  props: {
    pickleId: { type: String, default: null },
    onAllowReupload: { type: Function, default: null },
    onValid: { type: Function, default: null },
    onInvalid: { type: Function, default: null },
    onValidating: { type: Function, default: null },
    hideContext: { type: Boolean, default: false },
    hideLogs: { type: Boolean, default: false },
    showAll: { type: Boolean, default: false },
  },
  data() {
    return {
      clearIntervalKey: null,
      pollInterval: 30000,
      triggerStatus: undefined,
      triggerStatuses: null,
      validationStatus: null,
      showLogModal: false,
      triggerId: null,
      logs: null,
      errorDescription: null,
      pollCounter: 0,
    }
  },
  computed: {
    errorTriggerStatus() {
      return [null, 'model_failed', 'submission_failed', 'submission_error']
    },
    computedTriggerStatus() {
      if (this.hideContext) {
        return [this.triggerStatuses[this.triggerStatuses.length - 1]]
      } else {
        return this.triggerStatuses
      }
    },
  },
  created() {
    this.pollPickleValidationStatus()
  },
  beforeDestroy() {
    clearTimeout(this.clearIntervalKey)
  },
  methods: {
    ...mapActions(['showSnackBar']),
    pollPickleValidationStatus() {
      const fn = async () => {
        try {
          const res = await this.$client.getModelUploads({
            pickleId: this.pickleId,
            includeTriggerStatuses: true,
          })
          if (res.length) {
            this.handleTriggerStatus(res[0])
            this.handleValidationStatus(res[0])
            this.pollInterval = Math.min(
              this.pollIntervalForStatus(res[0].triggerStatus),
              this.pollIntervalForStatus(res[0].validationStatus),
            )
            this.pollCounter++
            this.clearIntervalKey = setTimeout(fn, this.pollInterval)
          } else {
            clearTimeout(this.clearIntervalKey)
          }
        } catch (e) {
          this.showSnackBar({
            msg: e.message,
          })
          clearTimeout(this.clearIntervalKey)
        }
      }
      fn()
      this.clearIntervalKey = setTimeout(fn, this.pollInterval)
    },
    async openTriggerLogs() {
      this.showLogModal = true
      this.logs = await this.$client.getTriggerLogs({
        triggerId: this.triggerId,
      })
    },
    handleTriggerStatus(pickle) {
      this.validationStatus = pickle.validationStatus
      this.triggerStatus = pickle.triggerStatus
      this.triggerStatuses = pickle.triggers.length
        ? pickle.triggers[pickle.triggers.length - 1].statuses.reverse()
        : null
      this.triggerId = pickle.triggers.length
        ? pickle.triggers[pickle.triggers.length - 1].id
        : null

      if (this.errorTriggerStatus.includes(this.triggerStatus)) {
        this.onAllowReupload && this.onAllowReupload()
        this.errorDescription =
          this.triggerStatuses && this.triggerStatuses.length
            ? this.triggerStatuses[this.triggerStatuses.length - 1].description
            : null
      }
    },
    handleValidationStatus(pickle) {
      if (this.triggerStatus === null) {
        clearTimeout(this.clearIntervalKey)
        this.onAllowReupload && this.onAllowReupload()
        return
      }

      const status = pickle.validationStatus

      switch (status) {
        case 'pending':
        case 'validating':
          this.onValidating && this.onValidating()
          break
        case 'invalid':
          clearTimeout(this.clearIntervalKey)
          this.onInvalid && this.onInvalid()
          break
        case 'validated':
          clearTimeout(this.clearIntervalKey)
          if (this.pollCounter > 0) {
            this.onValid && this.onValid()
          }
          break
        case 'failed':
          clearTimeout(this.clearIntervalKey)
          break
      }
    },
    getStatusClass(status, index) {
      if (this.errorTriggerStatus.includes(status)) {
        return 'error'
      } else if (
        status === 'submission_succeeded' ||
        (!this.hideContext && index < this.triggerStatuses.length - 1)
      ) {
        return 'success'
      } else {
        return 'active'
      }
    },
    formatStatusName(status) {
      switch (status) {
        case null:
          return 'Error'
        case 'queued':
          return 'Queued'
        case 'running':
          return 'Running'
        case 'model_succeeded':
          return 'Validating'
        case 'model_failed':
          return 'Failed'
        case 'submission_succeeded':
          return 'Success'
        case 'submission_failed':
          return 'Invalid'
        case 'submission_error':
          return 'Error'
      }
    },
    statusText(status) {
      switch (status) {
        case 'queued':
          return 'Your model is queued to run.'
        case 'running':
          return 'Your model is now running.'
        case 'model_succeeded':
          return 'Your submission is now being validated.'
        case 'submission_succeeded':
          return 'Your submission has been accepted.'
        case 'submission_failed':
          return 'Your submission was invalid.'
        case 'submission_error':
          return 'Something went wrong. Please contact support.'
      }
    },
    pollIntervalForStatus(status) {
      switch (status) {
        case 'pending':
        case 'queued':
        case 'running':
        case 'model_succeeded':
          return 10000
        case 'model_failed':
        case 'submission_succeeded':
        case 'submission_failed':
        case 'submission_error':
        case 'validating':
        case 'invalid':
        case 'validated':
        case 'failed':
        case null:
        default:
          return 30000
      }
    },
  },
}
</script>
<style scoped>
.rowContainer {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
</style>
